import { useState } from 'react';
import { Link, useParams } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from '../../lib/trpc';
import { idem } from '../../lib/idempotency';
import { PageRoot } from '../ui/Page';
import { BackCrumb, DetailHeader, MetaStrip, SectionCard } from '../ui/Detail';
import { StatusBadge, Notice } from '../ui/Badge';
import { Button } from '../ui/Button';
import { dash } from '@/components/ui/data-table';
import { EmptyState } from '../ui/EmptyState';
import { ErrorState } from '../ui/QueryStates';
import { PageSkeleton } from '../ui/Skeleton';
import { JOB_STATUS, display } from '../status';
import { WarrantyBadge, AgeLabel } from './WarrantyBadge';
import { EquipmentForm } from './EquipmentForm';
import { IconInventory, IconJobs } from '../icons';

/**
 * One unit (PRD v2 §6.7). The service history IS the page — a make and model without it is
 * a sticker somebody transcribed. Everything else is the header.
 */
export function EquipmentDetailPage() {
  const { equipmentId } = useParams({ from: '/office/equipment/$equipmentId' });
  const trpc = useTRPC();
  const qc = useQueryClient();
  const q = useQuery(trpc.equipment.get.queryOptions({ equipmentId }));
  const [editing, setEditing] = useState(false);

  const retire = useMutation(
    trpc.equipment.retire.mutationOptions({
      onSuccess: () => void qc.invalidateQueries(),
    }),
  );

  if (q.isPending) return <PageSkeleton />;
  if (q.isError) {
    return (
      <PageRoot>
        <BackCrumb to="/customers" label="Customers" />
        <ErrorState
          icon={IconInventory}
          what="this unit"
          onRetry={() => void q.refetch()}
        />
      </PageRoot>
    );
  }
  if (!q.data) {
    // Gone is not broken — no retry can bring it back.
    return (
      <PageRoot>
        <BackCrumb to="/customers" label="Customers" />
        <EmptyState
          icon={IconInventory}
          title="That unit isn't here"
          description="It may have been removed with its property, or the link is out of date."
        />
      </PageRoot>
    );
  }

  const { equipment: e, property, customer, history } = q.data;

  return (
    <PageRoot>
      <BackCrumb to="/customers" label="Customers" />
      <DetailHeader
        title={e.name}
        badge={
          <div className="flex flex-wrap items-center gap-2">
            <WarrantyBadge
              partsExpiresOn={e.warrantyExpiresOn}
              laborExpiresOn={e.laborWarrantyExpiresOn}
            />
            {!e.active ? <StatusBadge tone="neutral">Retired</StatusBadge> : null}
          </div>
        }
        right={
          <>
            <Button variant="ghost" onClick={() => setEditing(true)}>
              Edit
            </Button>
            {e.active ? (
              <Button
                variant="ghost"
                disabled={retire.isPending}
                onClick={() => retire.mutate({ equipmentId, ...idem() })}
              >
                Retire
              </Button>
            ) : null}
          </>
        }
      />

      <MetaStrip
        items={[
          {
            label: 'Customer',
            value: (
              <Link
                to="/customers/$customerId"
                params={{ customerId: customer.id }}
                className="underline decoration-dotted"
              >
                {customer.displayName}
              </Link>
            ),
          },
          { label: 'Property', value: property.label },
          {
            label: 'Make/model',
            value: dash([e.make, e.model].filter(Boolean).join(' ')),
          },
          { label: 'Serial', value: dash(e.serial) },
          { label: 'Installed', value: <AgeLabel installedOn={e.installedOn} /> },
        ]}
      />

      {retire.error ? <Notice tone="danger">{retire.error.message}</Notice> : null}
      {!e.active ? (
        <Notice tone="info">
          This unit has been retired. Its history stays here — that's the whole reason it
          was written down.
        </Notice>
      ) : null}

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <SectionCard title={`Service history (${history.length})`}>
            {history.length === 0 ? (
              <EmptyState
                icon={IconJobs}
                title="No work recorded on this unit yet"
                description="Tag this unit on a job and it starts building a history — every visit, in one place, for as long as the unit lasts."
              />
            ) : (
              <ul className="divide-y divide-border text-sm">
                {history.map((h) => {
                  const s = display(JOB_STATUS, h.status);
                  const when = h.lastVisitAt ?? h.completedAt ?? h.createdAt;
                  return (
                    <li key={h.jobId} className="flex items-center gap-3 py-2.5">
                      <span className="w-24 shrink-0 text-xs text-muted-foreground">
                        {new Date(when).toLocaleDateString()}
                      </span>
                      <Link
                        to="/jobs/$jobId"
                        params={{ jobId: h.jobId }}
                        className="min-w-0 flex-1 truncate hover:underline"
                      >
                        <span className="text-muted-foreground">#{h.jobNumber}</span>{' '}
                        {h.title}
                      </Link>
                      <StatusBadge tone={s.tone}>{s.label}</StatusBadge>
                    </li>
                  );
                })}
              </ul>
            )}
          </SectionCard>
        </div>

        <div className="space-y-4">
          <SectionCard title="Warranty">
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between gap-3">
                <dt className="text-muted-foreground">Parts until</dt>
                <dd>{dash(e.warrantyExpiresOn)}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-muted-foreground">Your labor until</dt>
                <dd>{dash(e.laborWarrantyExpiresOn)}</dd>
              </div>
            </dl>
            {!e.warrantyExpiresOn && !e.laborWarrantyExpiresOn ? (
              <p className="mt-2 text-xs text-muted-foreground">
                Nothing recorded — which is not the same as out of warranty. Worth
                checking the paperwork before you quote a repair.
              </p>
            ) : null}
          </SectionCard>

          <SectionCard title="Where it is">
            <p className="text-sm">{property.label}</p>
            <p className="text-sm text-muted-foreground">
              {[property.address1, property.city, property.state]
                .filter(Boolean)
                .join(', ') || '—'}
            </p>
            {e.locationNote ? <p className="mt-2 text-sm">{e.locationNote}</p> : null}
          </SectionCard>

          {e.notes ? (
            <SectionCard title="Notes">
              <p className="whitespace-pre-wrap text-sm">{e.notes}</p>
            </SectionCard>
          ) : null}
        </div>
      </div>

      {editing ? (
        <SectionCard title="Edit this unit">
          <EquipmentForm
            existing={e}
            onDone={() => setEditing(false)}
            onCancel={() => setEditing(false)}
          />
        </SectionCard>
      ) : null}
    </PageRoot>
  );
}
